// src/Pages/Paciente/PagarLinkifyButton.jsx
import React, { useState } from "react";
import { db } from "../../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { obtenerSesionesEntrenamiento1x } from "../../utils/planesEntrenamiento1x";

const LINKIFY_ACCOUNT_ID = "g0XopmYBqJKa5jM"; // Merchant ID Linkify

/**
 * BOTÓN UNIVERSAL DE PAGO LINKIFY
 *
 * El único objetivo de este botón es:
 * 1. Crear un documento en /pagos (estado=pendiente)
 * 2. Redirigir a Linkify al pago remoto
 *
 * La reserva NO se crea aquí.
 * La reserva se crea automáticamente en backend al aprobarse el pago (webhook + onPagoUpdate).
 */

export default function PagarLinkifyButton({
  // Identidad del paciente REAL
  pacienteId,
  nombre,
  rut,
  email, // ← ESTE ES EL EMAIL DEL PACIENTE (el correcto)

  // Horario
  fecha,
  hora,

  // Servicio
  servicio_raw,
  servicio_categoria,
  servicio_variante,
  descripcion,

  // Pago
  montoCLP,
  pago_tipo,

  

  // Plan
  planId = null,
  desdePlan = false,

  // 🔥 NUEVO: cantidad de sesiones para saber si se aplica recargo
  cantidadSesiones = 1,
  varianteId = null,
  precioTotalPlan = null,
  primeraSesionPlan = false,
  modalidad = null,

  className = "",
  planEntrada,
}) {
  const [loading, setLoading] = useState(false);

  // ================================
  // CÁLCULO DE MONTO CON RECARGO
  // ================================
  let monto = Number(montoCLP || 0);

  // 🔥 Solo agregar $250 si el servicio es de 1 sola sesión
  if (cantidadSesiones === 1) {
    monto += 250;
  }

  // Datos EXCLUSIVOS de las tres variantes 1x, sin alterar el resto de Linkify.
  const sesiones1x = !planId
    ? obtenerSesionesEntrenamiento1x(servicio_categoria, varianteId)
    : null;

  const handleClick = async () => {
    if (loading) return;

    // Validaciones mínimas
    if (!email || !nombre) return alert("Faltan datos del paciente.");
    if (!fecha || !hora) return alert("Debes seleccionar fecha y hora.");
    if (!monto || monto <= 0) return alert("Monto inválido.");
    if (sesiones1x !== null && (!Number.isFinite(Number(precioTotalPlan)) || Number(precioTotalPlan) < monto)) {
      return alert("No se pudo verificar el precio completo del plan. Vuelve a seleccionar la variante.");
    }

    try {
      setLoading(true);

      // Limpiar RUT
      const rutLimpio = (rut || "").replace(/\./g, "").replace(/-/g, "");

      // =========================================
      // 1) Crear /pagos → documento pendiente
      // =========================================
      const pagoRef = await addDoc(collection(db, "pagos"), {
        // Identidad del paciente REAL
        pacienteId: pacienteId || null,
        uid: pacienteId || null, // requerido por firestore.rules para /pagos
        nombre: nombre || "",
        rut: rutLimpio,

        email_paciente: email,
        email_pago: email,

        // Horario
        fecha,
        hora,

        // Servicio
        servicio_raw: servicio_raw || "",
        servicio_categoria: servicio_categoria || "",
        servicio_variante: servicio_variante || "",
        descripcion: descripcion || servicio_raw || "",

        // Pago
        montoCLP: monto,
        monto,
        pago_tipo: pago_tipo || "completo",
        metodoPago: "linkify",
        origenPago: "linkify",

        // Plan
        planId: planId || null,
        desdePlan: !!planId,

        // Solo contrataciones nuevas de 1x: el backend necesita el precio
        // del plan COMPLETO además del abono recibido por Linkify.
        ...(sesiones1x !== null
          ? {
              varianteId,
              plan_sesiones_totales: sesiones1x,
              plan_precio_total: Number(precioTotalPlan || 0),
              plan_primera_sesion: !!primeraSesionPlan,
              modalidad: modalidad || null,
            }
          : {}),

        // 🔥 Correcto: marcar si es pareja por servicio O por plan existente
        es_pareja:
          !!servicio_categoria?.toLowerCase().includes("pareja") ||
          !!servicio_variante?.toLowerCase().includes("pareja") ||
          !!desdePlan,   // si el plan ya es de pareja, se mantiene

        plan_es_pareja: desdePlan
        ? true
        : (
            servicio_categoria?.toLowerCase().includes("pareja") ||
            servicio_variante?.toLowerCase().includes("pareja")
          ),

        // ===============================
        // 🔵 METADATA COMPLETA DE PAREJA
        // ===============================
        ...(planEntrada?.es_pareja
          ? {
              titular_uid: planEntrada.titular_uid || pacienteId || null,
              acompanante_uid: planEntrada.acompanante_uid || null,
              acompanante_info: planEntrada.acompanante_info || null,
            }
          : {}),

        // Estado
        estado: "pendiente",
        createdAt: serverTimestamp(),
      });


      const remoteId = pagoRef.id;

      // =========================================
      // 2) Redirigir a Linkify
      // =========================================
      window.location.href = `https://app.linkify.cl/pay/${LINKIFY_ACCOUNT_ID}/remote/${remoteId}`;

    } catch (err) {
      console.error("❌ Error iniciando pago Linkify:", err);
      alert("Error iniciando pago. Intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={
        className ||
        "w-full border border-gray-300 rounded-xl p-4 text-left hover:bg-blue-50/40 transition disabled:opacity-50"
      }
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-blue-900">
            {loading
              ? "Conectando validación automática..."
              : "Transferencia con validación automática con Linkify"}
          </div>

          <div className="text-sm text-gray-600 mt-0.5">
            Validación en tiempo real de tu transferencia y aprobación inmediata de tu reserva.
          </div>

          {/* Nota chica (recargo) SOLO si aplica */}
          {cantidadSesiones === 1 && (
            <div className="text-xs text-gray-600 mt-1">
              Incluye recargo de <strong>$250</strong> por uso de plataforma.
            </div>
          )}
        </div>

        <span className="shrink-0 px-3 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
          Recomendado
        </span>
      </div>
    </button>
  );
}
